import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { ReleaseNoteDto } from '@app/generated/model';
import { Subscription } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { ReleaseNoteService } from '@app/core/services/release-note.service';
import { take, filter } from 'rxjs/operators';
import { AddReleaseNoteComponent } from './add-release-notes/add-release-note.component';

@Component({
  selector: 'app-release-notes',
  templateUrl: './release-notes.component.html',
  styleUrls: ['./release-notes.component.scss'],
  standalone: false,
})
export class ReleaseNotesComponent implements OnInit, OnDestroy {
  displayColumns: string[] = ['version', 'date', 'type', 'title', 'description', 'actions'];
  dataSource: MatTableDataSource<ReleaseNoteDto> = new MatTableDataSource();
  subscriptions: Subscription;
  filter = '';
  loading: boolean = true;

  @ViewChild(MatSort, { static: false }) set matSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  constructor(
    private releaseNoteService: ReleaseNoteService,
    private dialog: MatDialog,
    private router: Router,
  ) {
    this.subscriptions = new Subscription();
  }

  ngOnInit() {
    this.loadReleaseNotes();

    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          filter((event: NavigationEnd) => event.urlAfterRedirects === '/admin/release-notes'),
        )
        .subscribe(() => {
          this.loadReleaseNotes();
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadReleaseNotes(): void {
    this.loading = true;
    this.releaseNoteService
      .getAllReleaseNotes()
      .pipe(take(1))
      .subscribe({
        next: (releaseNotes) => {
          this.dataSource.data = releaseNotes
            .map((r) => ({ ...r, date: r.date ? r.date.split('T')[0] : r.date }))
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          this.dataSource.filterPredicate = (data: ReleaseNoteDto, filter: string) => {
            const date = data.date ? data.date.split('T')[0] : '';
            return (
              data.version?.toLowerCase().includes(filter) ||
              data.type?.toLowerCase().includes(filter) ||
              data.title?.toLowerCase().includes(filter) ||
              data.description?.toLowerCase().includes(filter) ||
              date.includes(filter)
            );
          };
          this.loading = false;
        },
        error: (err) => {
          console.error('Error loading release notes:', err);
          this.loading = false;
        },
      });
  }

  onRefresh(): void {
    this.loadReleaseNotes();
  }

  onFilter(filter: string) {
    this.filter = filter;
    this.dataSource.filter = filter.trim().toLowerCase();
  }

  onAdd() {
    this.dialog
      .open(AddReleaseNoteComponent)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.loadReleaseNotes();
        }
      });
  }

  onEdit(releaseNote: ReleaseNoteDto) {
    this.router.navigateByUrl(`/admin/release-notes/${releaseNote.id}`);
  }
}
